import { Box, Button, Divider, Typography } from '@mui/material'
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google'
import { useLayoutEffect, useRef, useState } from 'react'
import { useLogin, useNotify, useTranslate } from 'react-admin'

import { microsoftEnabled, signInWithMicrosoft } from './microsoftAuth'

const googleClientId = import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID as string | undefined

/**
 * Social sign-in buttons (Google, Microsoft) rendered beneath the vetpo
 * email/password LoginForm. Each provider obtains an ID token client-side and
 * hands it to authProvider.login({ provider, idToken }), which exchanges it at
 * the backend's /api/users/oauth/login (context: 'staff').
 *
 * Renders nothing until at least one provider is configured (env vars), so the
 * feature is a no-op until the provider consoles are set up (see
 * docs/OAUTH_SETUP.md in ada-core).
 */
// Google's button is an iframe with a FIXED pixel width (its API allows
// 200-400). It used to be a hard 280px inside a padded box, so it overhung
// the 268px login inputs; measure the row instead so both share one edge.
const GOOGLE_MIN_WIDTH = 200
const GOOGLE_MAX_WIDTH = 400
const GOOGLE_FALLBACK_WIDTH = 280

export const OAuthButtons = () => {
  const login = useLogin()
  const notify = useNotify()
  const translate = useTranslate()
  const rowRef = useRef<HTMLDivElement>(null)
  const [buttonWidth, setButtonWidth] = useState(GOOGLE_FALLBACK_WIDTH)

  // Measure on mount AND whenever the row resizes: the iframe's pixel width cannot
  // follow its container, so a one-time measure left it overhanging after a phone
  // rotation to a narrower card.
  useLayoutEffect(() => {
    const el = rowRef.current
    if (!el) return
    const measure = () => {
      const w = el.clientWidth
      if (w) setButtonWidth(Math.max(GOOGLE_MIN_WIDTH, Math.min(GOOGLE_MAX_WIDTH, Math.floor(w))))
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  if (!googleClientId && !microsoftEnabled) return null

  const onGoogleCredential = (credential?: string) => {
    if (!credential) {
      notify('pos.auth.login.oauth_failed', { type: 'error' })
      return
    }
    // login() resolves → react-admin redirects to the dashboard automatically.
    login({ provider: 'google', idToken: credential }).catch((err) => {
      notify(err?.message || 'pos.auth.login.oauth_failed', { type: 'error' })
    })
  }

  const onMicrosoft = async () => {
    try {
      const idToken = await signInWithMicrosoft()
      await login({ provider: 'microsoft', idToken })
    } catch (err: any) {
      // Swallow the user-cancelled-popup case quietly; surface real failures.
      if (err?.errorCode === 'user_cancelled') return
      notify(err?.message || 'pos.auth.login.oauth_failed', { type: 'error' })
    }
  }

  return (
    <Box>
      <Divider sx={{ my: 2.5 }}>
        <Typography variant="caption" color="text.disabled">
          {translate('pos.login.or')}
        </Typography>
      </Divider>
      <Box
        ref={rowRef}
        sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'center' }}
      >
        {googleClientId && (
          <GoogleOAuthProvider clientId={googleClientId}>
            <GoogleLogin
              onSuccess={(resp) => onGoogleCredential(resp.credential)}
              onError={() => onGoogleCredential(undefined)}
              width={String(buttonWidth)}
            />
          </GoogleOAuthProvider>
        )}
        {microsoftEnabled && (
          <Button variant="outlined" fullWidth onClick={onMicrosoft}>
            {translate('pos.login.continue_microsoft')}
          </Button>
        )}
      </Box>
    </Box>
  )
}

export default OAuthButtons
